import 'react-tippy/dist/tippy.css';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { Tooltip } from 'react-tippy';
import { setTooltipGlobalOpen, setActionTooltipGlobalOpen } from 'src/redux/basic.slice';
import { RootState, store } from 'src/redux/store';

interface IProps {
    message?: any;
    children: any;
    openOnClick?: boolean;
    parentClassName?: string;
    isUseChildTopPos?: boolean;
    followCursor?: boolean;
    isOpenChildTopPosOnTop?: boolean;
    childrenClassName?: string;
    isArrow?: boolean;
    TooltipCustomClass?: string;
    isTooltipInteractive?: boolean;
    /**
     * For a hover tooltip whose content scrolls: keeps it up while the pointer travels from the
     * trigger onto the tooltip, so the scrollbar can be reached. Without it, the trigger's
     * mouseleave (and tippy's own) closes the tooltip the instant the pointer leaves it — which is
     * why every other interactive tooltip in the app has to use `openOnClick` instead.
     */
    isHoverPersistent?: boolean;
    onOpenChange?: (open: boolean) => void;
    style?: React.CSSProperties;
    position?: 'top' | 'bottom' | 'left' | 'right' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end';
}

const TooltipCmp = ({
    message,
    children,
    openOnClick,
    parentClassName = '',
    childrenClassName = '',
    isUseChildTopPos,
    isOpenChildTopPosOnTop,
    followCursor,
    isArrow = true,
    TooltipCustomClass = '',
    style,
    position,
    isTooltipInteractive,
    isHoverPersistent,

    onOpenChange,
}: IProps) => {
    const dispatch = useDispatch();
    const uiState = useSelector((state: any) => state.MobileView);
    const tooltipGlobalOpen = useSelector((state: RootState) => state.basic.tooltipGlobalOpen);
    const tooltipRef = useRef<any>(null);
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const [delayCloseTimeout, setDelayCloseTimeout] = useState<NodeJS.Timeout | null>(null);
    const [topPos, setTopPos] = useState(0);

    useEffect(() => {
        const handleScroll = (e: Event) => {
            if (tooltipOpen || tooltipGlobalOpen) {
                // Ignore scrolls happening INSIDE the tooltip itself (interactive content)
                const target = e.target as HTMLElement | null;

                if (target?.closest?.('.tippy-popper')) return;

                setTooltipOpen(false);
                dispatch(setTooltipGlobalOpen(false));
            }
        };

        const scrollableContainer = document.querySelector('.main__layout_section');
        scrollableContainer?.addEventListener('scroll', handleScroll);

        const allScrollableContainers = document.querySelectorAll('.overflow-x-auto');

        allScrollableContainers.forEach((element) => {
            element.addEventListener('scroll', handleScroll);
        });

        const allScrollableYContainers = document.querySelectorAll('.overflow-y-auto');

        allScrollableYContainers.forEach((element) => {
            element.addEventListener('scroll', handleScroll);
        });

        const allOverflowAutoContainers = document.querySelectorAll('.overflow-auto');

        allOverflowAutoContainers.forEach((element) => {
            element.addEventListener('scroll', handleScroll);
        });

        return () => {
            scrollableContainer?.removeEventListener('scroll', handleScroll);
            allScrollableContainers.forEach((element) => {
                element?.removeEventListener('scroll', handleScroll);
            });
            allScrollableYContainers.forEach((element) => {
                element.removeEventListener('scroll', handleScroll);
            });
            allOverflowAutoContainers.forEach((element) => {
                element.removeEventListener('scroll', handleScroll);
            });
        };
    }, [tooltipOpen, message, children]);

    const delayTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const hoverCloseTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const cancelHoverClose = () => {
        if (hoverCloseTimeoutRef.current) {
            clearTimeout(hoverCloseTimeoutRef.current);
            hoverCloseTimeoutRef.current = null;
        }
    };

    useEffect(() => cancelHoverClose, []);

    // This function used to render floating tooltip on bottom instead of top
    const handleToggleTooltip = (e: any) => {
        e.stopPropagation();
        const targetHeight = e.target.clientHeight && e.target.clientHeight !== 0 ? e.target.clientHeight : 20;
        const extraPadding = 15;
        setTopPos(targetHeight + extraPadding);
        const next = !tooltipOpen;
        setTooltipOpen(next);
        onOpenChange?.(next);
        dispatch(setActionTooltipGlobalOpen(false));
    };

    const handleMouseEnter = () => {
        cancelHoverClose();

        if (delayTimeoutRef.current) {
            clearTimeout(delayTimeoutRef.current);
        }

        delayTimeoutRef.current = setTimeout(() => {
            setTooltipOpen(true);
            dispatch(setTooltipGlobalOpen(true));
            dispatch(setActionTooltipGlobalOpen(false));
        }, 200);
    };

    const closeTooltip = () => {
        dispatch(setTooltipGlobalOpen(false));
        setTooltipOpen(false);
    };

    const handleMouseLeave = () => {
        if (delayTimeoutRef.current) {
            clearTimeout(delayTimeoutRef.current);
        }

        if (isHoverPersistent) {
            // Grace period so the pointer can cross the gap between the trigger and the tooltip.
            cancelHoverClose();
            hoverCloseTimeoutRef.current = setTimeout(closeTooltip, 150);

            return;
        }

        closeTooltip();
    };

    useEffect(() => {
        if (!uiState.isMobile && !isUseChildTopPos) {
            return;
        }

        if (tooltipGlobalOpen) {
            return;
        }

        if (delayCloseTimeout) {
            clearTimeout(delayCloseTimeout);
        }

        setDelayCloseTimeout(
            setTimeout(() => {
                setTooltipOpen(false);
                dispatch(setTooltipGlobalOpen(false));
            }, 300)
        );
    }, [tooltipGlobalOpen]);

    return (
        <>
            {isTooltipInteractive &&
                tooltipOpen &&
                createPortal(
                    <div
                        className="fixed inset-0 z-[9998]"
                        onClick={(e) => {
                            e.stopPropagation();
                            setTooltipOpen(false);
                            dispatch(setTooltipGlobalOpen(false));
                        }}
                    />,
                    document.body
                )}
            {/* @ts-ignore */}
            <Tooltip
                ref={tooltipRef}
                style={style}
                appendTo={document.body}
                popperOptions={{
                    modifiers: {
                        addCustomClass: {
                            enabled: true,
                            fn: (data: any) => {
                                if (TooltipCustomClass) {
                                    data.instance.popper.classList.add(TooltipCustomClass);
                                }

                                if (!isArrow) {
                                    data.instance.popper.classList.add('tippy-no-arrow');
                                }

                                return data;
                            },
                        },
                        preventOverflow: {
                            enabled: true,
                            boundariesElement: 'viewport',
                        },
                        flip: {
                            enabled: true,
                            boundariesElement: 'viewport',
                        },
                    },
                }}
                html={
                    <Provider store={store}>
                        <div
                            style={isUseChildTopPos ? (isOpenChildTopPosOnTop ? { bottom: `0px` } : { top: `${topPos}px` }) : {}}
                            className={`${isUseChildTopPos ? 'absolute left-1/2 transform -translate-x-[80%]' : ''}  ${parentClassName} tooltipV3 `}
                            onMouseEnter={isHoverPersistent ? cancelHoverClose : undefined}
                            onMouseLeave={isHoverPersistent ? handleMouseLeave : undefined}
                        >
                            {message}
                        </div>
                    </Provider>
                }
                interactive={isHoverPersistent}
                followCursor={!openOnClick && followCursor}
                theme="light"
                duration={0}
                open={message ? tooltipOpen : false}
                arrow={isArrow}
                onShown={() => {
                    setTooltipOpen(true);
                    dispatch(setTooltipGlobalOpen(true));
                    dispatch(setActionTooltipGlobalOpen(false));
                    onOpenChange?.(true);
                }}
                onHidden={() => {
                    setTooltipOpen(false);
                    dispatch(setTooltipGlobalOpen(false));
                    onOpenChange?.(false);
                }}
                hideOnClick={false}
                delay={200}
                trigger={`${uiState.isMobile ? 'manual' : openOnClick ? 'click' : 'mouseenter'}`}
                position={position || (isOpenChildTopPosOnTop === false ? 'top' : 'top')}
            >
                <div
                    className={`${childrenClassName} inline`}
                    onClick={openOnClick ? handleToggleTooltip : undefined}
                    onMouseEnter={openOnClick ? () => false : handleMouseEnter}
                    onMouseLeave={openOnClick ? () => false : handleMouseLeave}
                >
                    {children}
                </div>
            </Tooltip>
        </>
    );
};

export default TooltipCmp;
